import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { calculateQuote, formatCents, type QuoteCalculationResult } from '@shared/calculations/pricing.ts';
import { areaPricingCalculation, HOURLY_AREA_PRICING } from '@shared/area-pricing.ts';
import type { SeriesFrequency } from '@shared/constants/index.ts';
import { photoQuoteRange } from '@shared/photo-quotes.ts';
import type { Quote, QuoteSummary, QuoteValueSum } from '@shared/types/index.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { firstVisitPreview, quoteFirstCleanPrice } from '@shared/first-clean-price.ts';
import { getDateLocale } from '../../i18n/locale.ts';

/**
 * What the customer pays, in one sentence.
 *
 * The running total is the number. The cadence says how often that number
 * happens: the accepted tier once a customer has locked one in, and the
 * preferred answer only before that. A frequency option with its own
 * discount uses that tier's price. A manual price wins over both.
 */
function cadenceOf(quote: Quote): SeriesFrequency | null {
  return quote.acceptedFrequency ?? quote.preferredFrequency ?? null;
}

/**
 * The offered (or accepted) frequency's own price, when that offer changes
 * the quote. Null when the headline is just the preview.
 *
 * Before acceptance the discount applies only while the offer is on. After
 * acceptance the sold tier is the price, even when the offer flag is off
 * or the estimator's preferred cadence was a different one.
 */
function tierCalculation(quote: Quote): QuoteCalculationResult | null {
  const frequency = cadenceOf(quote);
  const accepted = quote.acceptedFrequency != null;
  const tier =
    frequency && (accepted || quote.frequencyTiersEnabled)
      ? quote.frequencyTiers?.find((item) => item.frequency === frequency)
      : undefined;
  if (!tier?.discountType) return null;
  return calculateQuote({
    ...calculationInputFor(quote),
    frequencyTierDiscount: { discountType: tier.discountType, discountValue: tier.discountValue },
  });
}

/** The calculation behind the headline, so a breakdown can use the same tax. */
export function customerCalculation(quote: Quote, preview: QuoteCalculationResult): QuoteCalculationResult {
  return tierCalculation(quote) ?? preview;
}

/**
 * The first visit's price while the quote has a first clean price, worked out
 * the way the quote save builds the offer. Null without one, and on a quote
 * that cannot carry one (frequency choices, a range, a total override).
 */
export function firstVisitCents(quote: Quote): number | null {
  const price = quote.billingBasis === 'per_visit' ? quoteFirstCleanPrice(quote) : null;
  if (
    !price ||
    // A one-time clean has no regular visits to follow the first.
    !quote.preferredFrequency ||
    quote.preferredFrequency === 'one_time' ||
    quote.frequencyTiersEnabled ||
    quote.pricePromise === 'range' ||
    quote.totals.totalOverrideCents != null
  )
    return null;
  const regular = calculateQuote(calculationInputFor(quote));
  const first = calculateQuote(calculationInputFor(quote, 'first_visit'));
  return firstVisitPreview(regular, first, price, quote.totals.taxRateBasisPoints, quote.currencyCode).totalCents;
}

export function customerPaysCents(quote: Quote, previewTotalCents: number): number {
  if (quote.totals.totalOverrideCents != null) return quote.totals.totalOverrideCents;
  return tierCalculation(quote)?.totalCents ?? previewTotalCents;
}

/**
 * A quote's price as a list shows it. A quote sent from a photo quote as a
 * price range shows the band the customer was given: its total is only the
 * band's middle, and the final price is set at the visit.
 */
export function quoteAmount(
  quote: Pick<
    QuoteSummary,
    'totalCents' | 'currencyCode' | 'pricePromise' | 'priceRangeLowCents' | 'priceRangeHighCents' | 'pricingBlocked'
  >,
  t: TFunction,
): string {
  if (quote.pricingBlocked) return t('areaPricing.unpriced');
  const locale = getDateLocale();
  if (quote.pricePromise === 'range' && quote.priceRangeLowCents != null && quote.priceRangeHighCents != null) {
    return t('quotes.list.range', {
      low: formatCents(quote.priceRangeLowCents, quote.currencyCode, locale),
      high: formatCents(quote.priceRangeHighCents, quote.currencyCode, locale),
    });
  }
  return formatCents(quote.totalCents, quote.currencyCode, locale);
}

/**
 * A sum of quote values, as the dashboards and reports show it: one amount,
 * or — when range quotes are in it — the band from its low end to its high
 * end, written the way a single range quote is in the quotes list. Adding a
 * range quote's stored middle as if it were a price would show money nobody
 * was promised.
 */
export function quoteValueAmount(sum: QuoteValueSum, currencyCode: string, t: TFunction): string {
  const locale = getDateLocale();
  if (sum.lowCents === sum.highCents) return formatCents(sum.totalCents, currencyCode, locale);
  return t('quotes.list.range', {
    low: formatCents(sum.lowCents, currencyCode, locale),
    high: formatCents(sum.highCents, currencyCode, locale),
  });
}

/**
 * The band a range quote shows for the price the editor has now. The stored
 * band while that is still the stored total; otherwise the band saving gives,
 * worked out from the width the way the server moves it.
 */
export function rangeBandFor(
  quote: Pick<
    Quote,
    'pricePromise' | 'priceRangeLowCents' | 'priceRangeHighCents' | 'priceRangeWidthPct' | 'totalCents'
  >,
  totalCents: number,
): { lowCents: number; highCents: number } | null {
  if (quote.pricePromise !== 'range') return null;
  const stored =
    quote.priceRangeLowCents != null && quote.priceRangeHighCents != null
      ? { lowCents: quote.priceRangeLowCents, highCents: quote.priceRangeHighCents }
      : null;
  if (stored && totalCents === quote.totalCents) return stored;
  if (typeof quote.priceRangeWidthPct === 'number') return photoQuoteRange(totalCents, quote.priceRangeWidthPct);
  return stored;
}

export function plainPriceSentence(quote: Quote, amount: string, t: TFunction): string {
  if (
    quote.pricingBlocked ||
    areaPricingCalculation(
      quote.areaPricing ?? HOURLY_AREA_PRICING,
      quote.billingBasis === 'monthly',
      quote.currencyCode,
    ).pricingBlocked
  )
    return t('areaPricing.unpriced');
  if (quote.acceptedFirstClean) return `${t('firstClean.approved')}: ${quote.acceptedFirstClean.name}`;
  const first = firstVisitCents(quote);
  if (first !== null)
    return t('firstClean.summary', { first: formatCents(first, quote.currencyCode, getDateLocale()), regular: amount });
  // Alternatives from separate quotes, from before the first clean price.
  if (quote.firstClean) return t('firstClean.legacyHeading');
  if (quote.billingBasis === 'monthly') return t('quoteBuilder.payMonth', { amount });
  const frequency = cadenceOf(quote);
  if (!frequency || frequency === 'one_time') return t('quoteBuilder.payOnce', { amount });
  return t('quoteBuilder.payVisit', { amount, when: whenPhrase(frequency, t) });
}

function whenPhrase(frequency: Exclude<SeriesFrequency, 'one_time'>, t: TFunction): string {
  if (frequency === 'weekly') return t('quoteBuilder.when.weekly');
  if (frequency === 'biweekly') return t('quoteBuilder.when.biweekly');
  return t('quoteBuilder.when.monthly');
}

export function PlainPrice({
  quote,
  previewTotalCents,
  currencyCode,
}: {
  quote: Quote;
  previewTotalCents: number;
  currencyCode: string;
}) {
  const { t } = useTranslation();
  if (
    areaPricingCalculation(
      quote.areaPricing ?? HOURLY_AREA_PRICING,
      quote.billingBasis === 'monthly',
      quote.currencyCode,
    ).pricingBlocked
  )
    return <p className="text-lg font-bold">{t('areaPricing.unpriced')}</p>;
  const cents = customerPaysCents(quote, previewTotalCents);
  const band = rangeBandFor(quote, cents);
  const amount = band
    ? t('quotes.list.range', {
        low: formatCents(band.lowCents, currencyCode, getDateLocale()),
        high: formatCents(band.highCents, currencyCode, getDateLocale()),
      })
    : formatCents(cents, currencyCode, getDateLocale());
  return (
    <>
      <p className="text-lg font-bold">{plainPriceSentence(quote, amount, t)}</p>
      {band ? <p className="text-sm text-[color:var(--color-muted)]">{t('quoteBuilder.rangeNote')}</p> : null}
    </>
  );
}
